import React, {useState} from 'react';

const FALLBACK_COVER = '/images/cover-placeholder.jpg';
const DETAILS_KEY = 'abtunes_details';

const pct = (p) => Math.round((p || 0) * 100) + '%';

const loadDetails = () => {
    try {
        return localStorage.getItem(DETAILS_KEY) === '1';
    } catch (e) {
        return false;
    }
};

/** Tester metadata for a queued song, compared against what's playing. */
function Compare({s, now}) {
    const bpmDelta = s.bpm && now.bpm ? s.bpm - now.bpm : null;
    const sameKey = s.song_key && now.song_key && s.song_key === now.song_key;
    const sameGenre = s.genre && now.genre && s.genre === now.genre;
    const chip = (label, strong) => (
        <span className={'rounded-md px-1.5 py-0.5 ' + (strong ? 'bg-white/[0.14] text-white/85' : 'bg-white/[0.06] text-white/50')}>
            {label}
        </span>
    );
    return (
        <span className="mt-1.5 flex flex-wrap gap-1 font-mono text-[10.5px]">
            {s.genre && chip(s.genre, sameGenre)}
            {s.year && chip(s.year)}
            {s.bpm && chip(s.bpm + ' bpm' + (bpmDelta !== null ? ' (' + (bpmDelta > 0 ? '+' : '') + bpmDelta + ')' : ''),
                bpmDelta !== null && Math.abs(bpmDelta) <= 5)}
            {s.song_key && chip(s.song_key + (sameKey ? ' · same key' : ''), sameKey)}
            {s.album && chip(s.album)}
            {s.score_parts && chip('sound ' + pct(s.score_parts.sound)
                + (s.score_parts.genre ? ' +' + Math.round(s.score_parts.genre * 100) + ' genre' : '')
                + (s.score_parts.era ? ' +' + Math.round(s.score_parts.era * 100) + ' era' : ''), true)}
            {s.is_mutual && chip('mutual', true)}
            {s.is_live && chip('live')}
            {s.is_remix && chip('remix')}
        </span>
    );
}

/**
 * The listening room: what's playing holds the top of the page, the next
 * song is presented on its own with its match score, and the rest of the
 * path follows. "Details" reveals the tester metadata (genre, BPM, key,
 * raw score) so matches can be validated without cluttering the default view.
 */
export default function ListeningRoom({engine, onArtist, onAlbum, onPlayFromQueue}) {
    const {playingSong, playerQueue, title, artist, coverUrl} = engine;
    const [details, setDetails] = useState(loadDetails);

    const toggleDetails = () => {
        const next = !details;
        setDetails(next);
        try { localStorage.setItem(DETAILS_KEY, next ? '1' : '0'); } catch (e) { /* noop */ }
    };

    const queue = Array.isArray(playerQueue) ? playerQueue : [];
    const idx = queue.findIndex((s) => s.id === playingSong.id);
    const upcoming = [];
    for (let i = 1; i <= 8 && queue.length > 1; i++) {
        upcoming.push(queue[(idx + i) % queue.length]);
    }
    const [next, ...later] = upcoming;

    const meta = [playingSong.year, playingSong.genre].filter(Boolean).join(' · ');
    const version = playingSong.is_live ? 'Live' : playingSong.is_remix ? 'Remix' : 'Original';

    return (
        <div className="mx-auto w-full max-w-[760px] py-2">
            {/* Now playing */}
            <section className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-end sm:gap-8 sm:text-left">
                <img
                    key={coverUrl || 'cv'}
                    src={coverUrl || playingSong.cover || FALLBACK_COVER}
                    alt=""
                    className="h-[240px] w-[240px] flex-none animate-ambientfade rounded-3xl object-cover shadow-[0_40px_90px_rgba(0,0,0,0.6)] sm:h-[270px] sm:w-[270px]"
                />
                <div className="min-w-0 flex-1 pb-1">
                    <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-apple-red">Now playing</div>
                    <h1 className="font-heading mt-2 text-[32px] font-bold leading-[1.05] tracking-[-0.02em] sm:text-[44px]">
                        {title || playingSong.songTitle}
                    </h1>
                    <button type="button" onClick={() => artist && onArtist(artist)}
                            className="mt-2 text-[17px] text-white/70 transition hover:text-white hover:underline">
                        {artist}
                    </button>
                    {playingSong.album && (
                        <div>
                            <button type="button"
                                    onClick={() => playingSong.album_apple_id && onAlbum(playingSong.album_apple_id)}
                                    className="text-[13.5px] text-white/[0.45] transition hover:text-white/80 hover:underline">
                                {playingSong.album}
                            </button>
                        </div>
                    )}
                    <div className="mt-3 flex items-center justify-center gap-3 sm:justify-start">
                        {meta && <span className="text-[12.5px] text-white/40">{meta}</span>}
                        <button type="button" onClick={toggleDetails}
                                className={'rounded-full px-2.5 py-1 text-[11px] font-bold transition '
                                    + (details ? 'bg-white/[0.16] text-white' : 'bg-white/[0.07] text-white/50 hover:text-white')}>
                            <i className="fa-solid fa-sliders mr-1.5 text-[10px]"/>Details
                        </button>
                    </div>
                    {details && (
                        <div className="mt-3 grid grid-cols-3 gap-x-5 gap-y-2 rounded-2xl bg-white/[0.05] p-3.5 text-left font-mono text-[11px] sm:grid-cols-4">
                            {[
                                ['Genre', playingSong.genre],
                                ['Year', playingSong.year],
                                ['BPM (est.)', playingSong.bpm],
                                ['Key (est.)', playingSong.song_key],
                                ['Version', version],
                                ['Catalog', playingSong.id],
                                ['ISRC', playingSong.isrc],
                            ].map(([k, v]) => (
                                <div key={k} className="min-w-0">
                                    <div className="text-[9.5px] uppercase tracking-[0.08em] text-white/30">{k}</div>
                                    <div className="truncate text-white/80">{v ?? '—'}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Up next */}
            {next ? (
                <section className="mt-12">
                    <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/[0.38]">Up next</div>
                    <button type="button" onClick={() => onPlayFromQueue(next, queue)}
                            className="mt-3 flex w-full items-center gap-4 rounded-2xl bg-white/[0.07] p-3.5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_24px_50px_rgba(0,0,0,0.35)] backdrop-blur-xl transition hover:bg-white/[0.10]">
                        <img src={next.cover || FALLBACK_COVER} alt=""
                             className="h-[76px] w-[76px] flex-none rounded-xl object-cover shadow-[0_14px_30px_rgba(0,0,0,0.45)]"/>
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-[17px] font-bold">{next.title}</span>
                            <span className="block truncate text-[13px] text-white/50">
                                {next.artist}{next.year ? ' · ' + next.year : ''}
                            </span>
                            {details && <Compare s={next} now={playingSong}/>}
                        </span>
                        {next.proximity != null && (
                            <span className="flex-none text-right">
                                <span className="block text-[26px] font-extrabold leading-none tabular-nums text-[#ff97a5]">{pct(next.proximity)}</span>
                                <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-white/35">match</span>
                            </span>
                        )}
                    </button>
                </section>
            ) : (
                <p className="mt-12 text-[13px] text-white/40">Finding sonic neighbors…</p>
            )}

            {/* Then */}
            {later.length > 0 && (
                <section className="mt-8">
                    <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-white/[0.38]">Then</div>
                    <div className="mt-2">
                        {later.map((s, i) => (
                            <button key={s.id + '-' + i} type="button" onClick={() => onPlayFromQueue(s, queue)}
                                    className="flex w-full items-center gap-3.5 rounded-xl p-2 text-left transition hover:bg-white/[0.06]">
                                <img src={s.cover || FALLBACK_COVER} alt="" loading="lazy"
                                     className="h-11 w-11 flex-none rounded-lg object-cover"/>
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-[13.5px] font-semibold">{s.title}</span>
                                    <span className="block truncate text-[11.5px] text-white/[0.45]">
                                        {s.artist}{s.year ? ' · ' + s.year : ''}
                                    </span>
                                    {details && <Compare s={s} now={playingSong}/>}
                                </span>
                                {s.proximity != null && (
                                    <span className="flex-none rounded-lg bg-white/10 px-2 py-1 text-[11px] font-extrabold tabular-nums text-white/70">
                                        {pct(s.proximity)}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
